import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Employee } from '@model/employee';
import { Reservation, Table } from '@model/reservation';
import { ClickEvent, DragEndEvent, DragMoveEvent, DragStartEvent, DragTableHelper, Section, TableConnection, TablesMode } from '@model/tables-plan';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import { State } from '@store/index';
import * as EmployeeSelectors from '@store/selectors/employee.selectors';
import * as ReservationListSelectors from '@store/selectors/reservation-list.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Toast } from 'bootstrap';
import { Observable, Subject, fromEvent, takeUntil, tap } from 'rxjs';
import { CapacitorStorageService } from 'src/app/lib/capacitor-storage.service';
import { Common } from 'src/app/lib/common-methods';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { TablesPlanService } from '../tables-plan.service';
import { TableConnectionsService } from './table-connections.service';
import { TableComponent } from './table/table.component';
declare const bootstrap: any;
declare let $: any;

interface Position {
  x?: number;
  y?: number;
  length?: number;
}

export interface Offset {
  top: number;
  left: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface ScrollDistance {
  scrollTop: number;
  scrollLeft: number;
}

@Component({
  selector: 'app-canvas',
  templateUrl: './canvas.component.html',
  styleUrl: './canvas.component.scss',
})
export class CanvasComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private translate: TranslateService,
    private store: Store<State>,
    private tableConnectionsService: TableConnectionsService,
    private tablesPlanService: TablesPlanService,
    private capacitorStorageService: CapacitorStorageService,
    private storage: LocalStorageService,
  ) {

  }

  destroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild('toast') toastElement!: ElementRef;

  toast: Toast | null = null;

  editMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectEditMode));
  editMode: boolean = false;
  showTablesConnection$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectShowTablesConnection));
  showTablesConnection: boolean = false;
  workingDateSections$: Observable<Section[] | null> = this.store.pipe(select(TablesPlanSelectors.selectWorkingDateSections));
  workingDateSections: Section[] | null = null;
  date$: Observable<Date> = this.store.pipe(select(ReservationListSelectors.selectDate));
  date: Date = new Date();
  sectionTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSectionTables));
  sectionTables: Table[] = [];
  zoom$: Observable<number> = this.store.pipe(select(TablesPlanSelectors.selectZoom));
  zoom: number = 0.8;
  selectedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectSelectedTables));
  selectedTables: Table[] = [];
  allocatedTables$: Observable<Table[]> = this.store.pipe(select(TablesPlanSelectors.selectAllocatedTables));
  allocatedTables: Table[] = [];
  reservationAssignTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectReservationAssignTablesMode));
  reservationAssignTablesMode: boolean = false;
  addJoinedTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAddJoinedTablesMode));
  addJoinedTablesMode: boolean = false;
  assignCategoriesToTablesMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectAssignCategoriesToTablesMode));
  assignCategoriesToTablesMode: boolean = false;
  isOnExcludeFromOnlineChannelsMode$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectIsOnExcludeFromOnlineChannelsMode));
  isOnExcludeFromOnlineChannelsMode: boolean = false;
  assignTablesReservation$: Observable<Reservation | null> = this.store.pipe(select(TablesPlanSelectors.selectAssignTablesReservation));
  assignTablesReservation: Reservation | null = null;
  reservationsFilteredByShift$: Observable<Reservation[]> = this.store.pipe(select(TablesPlanSelectors.selectReservationsFilteredByShift));
  reservationsFilteredByShift: Reservation[] = [];
  sidePanelCollapsed$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectSidePanelCollapsed));
  sidePanelCollapsed: boolean = false;
  resetAllocatedTablesOnSelection$: Observable<boolean> = this.store.pipe(select(TablesPlanSelectors.selectResetAllocatedTablesOnSelection));
  resetAllocatedTablesOnSelection: boolean = false;
  editingTable$: Observable<{ table: Table, isNew: boolean } | null> = this.store.pipe(select(TablesPlanSelectors.selectEditingTable));
  editingTable: { table: Table, isNew: boolean } | null = null;
  loggedInemployee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  loggedInemployee: Employee | null = null;
  tablesMode$: Observable<TablesMode> = this.store.pipe(select(TablesPlanSelectors.selectTablesMode));
  tablesMode: TablesMode = 'Tables';
  tablesConnections: TableConnection[] = [];


  dragging: boolean = false;
  dragTableHelpers: DragTableHelper[] = [];

  updateTablesConnectionsTimeoutId: null | ReturnType<typeof setTimeout> = null;
  autoFitTimeoutId: null | ReturnType<typeof setTimeout> = null;
  autoFitDone: boolean = false;

  validationMessage: string | null = '';

  @HostListener('document:keydown', ['$event'])
  onKeyPress(event: KeyboardEvent) {

    // ctrl + '+'
    if (event.key === '+'
      || (event.key === '=' && event.shiftKey)) {
      if (event.ctrlKey) {
        event.preventDefault();
        this.zoomIn();
      }
    }

    // ctrl + '-'
    if (event.key === '-') {
      if (event.ctrlKey) {
        event.preventDefault();
        this.zoomOut();
      }
    }
  }

  touches: number = 0;
  previousPosition: Position = {};

  // prevent context menu on long tap on touch devices
  @HostListener('window:contextmenu ', ['$event'])
  handleOncontextmenu(event: any) {
    event.preventDefault();
  }

  // https://github.com/TimUnderhay/ngx-panzoom/blob/master/projects/ngx-panzoom/src/lib/panzoom.component.ts
  // https://material.angular.io/cdk/drag-drop/api#CdkDragMove

  // @HostListener('document:touchstart', ['$event'])
  // @HostListener('document:touchmove', ['$event'])
  @HostListener('document:touchend', ['$event'])
  @HostListener('document:touchcancel', ['$event'])
  handleTouch(event: any) {
    // event.preventDefault();

    this.touches = event.touches.length;

    // let pinchAmount = $event.scale - 1;
    // let scale = this.scale + pinchAmount * this.scaleFactor;
    // scale = clamp(scale, 1, this.zoomThreshold + this.thresholdBuffer);
    // this.calculatePinch(scale);
  }

  @HostListener('document:touchstart', ['$event'])
  handleTouchStart(event: any) {
    // event.preventDefault();
    // console.log('PanZoomComponent: onTouchStart()', event);
    // console.log('PanZoomComponent: onTouchStart(): touches:', event.touches.length);

    if (event.touches.length !== 1) {
      // multiple touches, get ready for zooming

      // Calculate x and y distance between touch events
      const x = event.touches[0].pageX - event.touches[1].pageX;
      const y = event.touches[0].pageY - event.touches[1].pageY;

      // Calculate length between touch points with pythagoras
      // There is no reason to use Math.pow and Math.sqrt as we
      // only want a relative length and not the exact one.
      this.previousPosition = {
        length: x * x + y * y
      };
    }
    // this.onMousedown(event);
  }

  @HostListener('document:touchmove', ['$event'])
  handleTouchMove(event: any) {
    // event.preventDefault();
    // console.log('PanZoomComponent: onTouchMove()');
    // console.log('PanZoomComponent: onTouchMove(): event:', event);

    if (event.touches.length === 1) {
      // single touch, emulate mouse move
      // this.onMouseMove(event);
    } else {
      // multiple touches, zoom in/out
      // console.log('pinch zooming');

      // Calculate x and y distance between touch events
      const x = event.touches[0].pageX - event.touches[1].pageX;
      const y = event.touches[0].pageY - event.touches[1].pageY;
      // Calculate length between touch points with pythagoras
      // There is no reason to use Math.pow and Math.sqrt as we
      // only want a relative length and not the exact one.
      const length = x * x + y * y;

      // Calculate delta between current position and last position
      const delta = length - (this.previousPosition?.length ?? 0);

      // Naive hysteresis
      if (Math.abs(delta) < 100) {
        return;
      }

      // this.changeZoomLevel(this.base.zoomLevel + delta * 0.0001, clickPoint);

      this.changeZoom(this.zoom + delta * 0.00001);

      // Update length for next move event
      this.previousPosition = {
        length: length
      };
    }
  }

  @ViewChild('canvas') set canvas(elRef: ElementRef) {
    this._canvas = elRef;
    this.canvasRect = elRef.nativeElement.getBoundingClientRect();
    this.store.dispatch({ type: TablesPlanActions.setCanvasRect.type, rect: this.canvasRect });
  }

  _canvas: ElementRef | null = null;
  canvasRect: DOMRect | null = null;
  get canvasScrollDistance(): ScrollDistance {
    return { scrollTop: this._canvas?.nativeElement.scrollTop, scrollLeft: this._canvas?.nativeElement.scrollLeft };
  }

  @ViewChildren('tables') tablesRef!: QueryList<TableComponent>;

  x1: number = 0;
  y1: number = 0;
  x2: number = 0;
  y2: number = 0;

  ax1: number = 0;
  ay1: number = 0;
  ax2: number = 0;
  ay2: number = 0;

  workingDateSectionsTimer: NodeJS.Timeout | undefined;

  ngOnInit(): void {

    this.editMode$.pipe(takeUntil(this.destroy$)).subscribe((editMode: boolean) => {
      this.editMode = editMode;
      this.updateTablesConnections();
    });

    this.showTablesConnection$.pipe(takeUntil(this.destroy$)).subscribe((showTablesConnection: boolean) => {
      this.showTablesConnection = showTablesConnection;
    });

    this.sectionTables$.pipe(takeUntil(this.destroy$)).subscribe((sectionTables: Table[]) => {
      this.sectionTables = sectionTables;

      // update lines
      this.updateTablesConnections();

      this.autoFit();
    });

    this.zoom$.pipe(takeUntil(this.destroy$)).subscribe((zoom: number) => {
      this.zoom = zoom;
    });

    this.selectedTables$.pipe(takeUntil(this.destroy$)).subscribe((selectedTables: Table[]) => {
      this.selectedTables = selectedTables;
    });

    this.allocatedTables$.pipe(takeUntil(this.destroy$)).subscribe((allocatedTables: Table[]) => {
      this.allocatedTables = allocatedTables;
    });

    this.reservationAssignTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((reservationAssignTablesMode: boolean) => {
      this.reservationAssignTablesMode = reservationAssignTablesMode;
      this.setAssignTablesMode();
    });

    this.addJoinedTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((addJoinedTablesMode: boolean) => {
      this.addJoinedTablesMode = addJoinedTablesMode;
    });

    this.assignCategoriesToTablesMode$.pipe(takeUntil(this.destroy$)).subscribe((assignCategoriesToTablesMode: boolean) => {
      this.assignCategoriesToTablesMode = assignCategoriesToTablesMode;
    });

    this.isOnExcludeFromOnlineChannelsMode$.pipe(takeUntil(this.destroy$)).subscribe((isOnExcludeFromOnlineChannelsMode: boolean) => {
      this.isOnExcludeFromOnlineChannelsMode = isOnExcludeFromOnlineChannelsMode;
    });

    this.assignTablesReservation$.pipe(takeUntil(this.destroy$)).subscribe((assignTablesReservation: Reservation | null) => {
      this.assignTablesReservation = assignTablesReservation;
      this.setAssignTablesMode();
    });

    this.reservationsFilteredByShift$.pipe(takeUntil(this.destroy$)).subscribe((reservationsFilteredByShift: Reservation[]) => {
      this.reservationsFilteredByShift = reservationsFilteredByShift;
      this.updateTablesConnections();
    });

    this.tablesMode$.pipe(takeUntil(this.destroy$)).subscribe((tablesMode) => {
      this.tablesMode = tablesMode;
    });

    this.date$.pipe(takeUntil(this.destroy$)).subscribe((date) => {
      this.date = date;
    });

    this.sidePanelCollapsed$.pipe(takeUntil(this.destroy$)).subscribe((sidePanelCollapsed: boolean) => {
      this.sidePanelCollapsed = sidePanelCollapsed;

      setTimeout(() => {
        // update canvas rect
        this.canvasRect = this._canvas?.nativeElement.getBoundingClientRect();

        // update lines
        this.updateTablesConnections();
      }, 10);

    });

    this.resetAllocatedTablesOnSelection$.pipe(takeUntil(this.destroy$)).subscribe((resetAllocatedTablesOnSelection: boolean) => {
      this.resetAllocatedTablesOnSelection = resetAllocatedTablesOnSelection;
    });

    this.editingTable$.pipe(takeUntil(this.destroy$)).subscribe((editingTable) => {
      this.editingTable = editingTable;
    });

    this.loggedInemployee$.pipe(takeUntil(this.destroy$)).subscribe((loggedInemployee) => {
      this.loggedInemployee = loggedInemployee;
    });

    this.workingDateSections$.pipe(takeUntil(this.destroy$)).subscribe((workingDateSections) => {

      if (workingDateSections == null) {
        this.workingDateSections = workingDateSections;
        return;
      }

      if (this.workingDateSectionsTimer != null) {
        clearTimeout(this.workingDateSectionsTimer);
      }

      this.workingDateSectionsTimer = setTimeout(() => {
        this.workingDateSections = workingDateSections;
      }, 10);

    });
  }

  ngAfterViewInit(): void {
    this.updateTablesConnections();

    this.toast = bootstrap.Toast.getOrCreateInstance(this.toastElement.nativeElement);

    // chrome fix: prevent browser's native zoom behavior
    // https://stackoverflow.com/questions/61114830/how-to-prevent-native-browser-default-pinch-to-zoom-behavior
    const wheelEvent$ = fromEvent(document, 'wheel', { passive: false });
    wheelEvent$.pipe(
      takeUntil(this.destroy$),
      tap((event: any) => {
        const { ctrlKey } = event;
        if (ctrlKey) {
          event.preventDefault();
        }
      })).subscribe();

    const resizeEvent$ = fromEvent(window, 'resize', { passive: false });
    resizeEvent$.pipe(
      takeUntil(this.destroy$),
      tap((event: any) => {
        this.canvasRect = this._canvas?.nativeElement.getBoundingClientRect();
      })).subscribe();
  }

  updateTablesConnections() {
    if (!this.showTablesConnection || this.editMode) {
      return;
    }

    if (this.updateTablesConnectionsTimeoutId != null) {
      clearTimeout(this.updateTablesConnectionsTimeoutId);
    }

    // table slots are hidden with *ngIf, give them some time to be added to dom again after state changes
    this.updateTablesConnectionsTimeoutId = setTimeout(() => {

      this.tablesConnections = this.tableConnectionsService.getConnections(
        this.sectionTables,
        this.tablesRef,
        this.canvasRect,
        this.canvasScrollDistance,
        this.zoom,
        this.reservationsFilteredByShift
      );

    }, 100);
  }

  zoomSliderChange(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    this.changeZoom(value);
  }

  zoomIn() {
    this.changeZoom(Common.round(this.zoom + 0.1, 1));
  }

  zoomOut() {
    this.changeZoom(Common.round(this.zoom - 0.1, 1));
  }

  changeZoom(zoom: number, roundValue: boolean = true) {
    if (zoom < 0.1) {
      zoom = 0.1;
    }
    if (zoom > 4) {
      zoom = 4;
    }

    if (roundValue) {
      zoom = Common.round(zoom, 1);
    }

    this.canvasRect = this._canvas?.nativeElement.getBoundingClientRect();

    this.store.dispatch({ type: TablesPlanActions.setZoom.type, zoom });
  }

  async autoFit() {

    if (this.autoFitDone) {
      return;
    }

    const zoom = await this.capacitorStorageService.getTablesPlanZoom();

    if (zoom != null) {
      return;
    }

    if (this.autoFitTimeoutId != null) {
      clearTimeout(this.autoFitTimeoutId);
    }

    this.autoFitTimeoutId = setTimeout(() => {
      if (this.autoFitDone) {
        return;
      }
      this.fitToScreen(true);
      this.autoFitDone = true;
    }, 1500);

  }

  fitToScreen(throttleMaxZoom: boolean = false) {
    const canvasWidth = this.canvasRect?.width ?? 1;
    const canvasHeight = this.canvasRect?.height ?? 1;

    // const scrollWidth = this._canvas?.nativeElement?.scrollWidth ?? canvasWidth;
    // const scrollHeight = this._canvas?.nativeElement?.scrollHeight ?? canvasHeight;

    const bounds = this.getBounds();

    const scaleX = canvasWidth / (bounds.left / this.zoom);
    const scaleY = canvasHeight / (bounds.top / this.zoom);

    let scale = Math.min(scaleX, scaleY);

    // throttle zoom to 1
    if (throttleMaxZoom === true && scale > 1) {
      scale = 1;
    }

    this.changeZoom(scale, false);
  }

  getBounds() {
    const canvasOffsetX = this.canvasRect?.x ?? 0;
    const canvasOffsetY = this.canvasRect?.y ?? 0;

    const left = Math.max(...this.tablesRef.map(ref => ref.tableRect.x - canvasOffsetX + ref.tableRect.width));
    const top = Math.max(...this.tablesRef.map(ref => ref.tableRect.y - canvasOffsetY + ref.tableRect.height));

    const scrollLeft = this._canvas?.nativeElement?.scrollLeft;
    const scrollTop = this._canvas?.nativeElement?.scrollTop;

    return { left: left + scrollLeft, top: top + scrollTop };
  }

  tableClick(event: ClickEvent) {
    const { table } = event;

    if (this.editMode) {

      if (this.tablesMode == 'JoinedTables') {

        if (!this.addJoinedTablesMode) {
          return;
        }

        const canJoin = this.tablesPlanService.tableCanBeJoined(table, this.allocatedTables);

        if (!canJoin) {
          // show warning
          this.validationMessage = this.translate.instant('tablesPlan.editMode.joinDifferentWarning', { name: this.allocatedTables.map(t => t.tableName).join(', ') });
          this.toast?.show();
          return;
        }

        // select/deselect table
        if (this.allocatedTables.some(t => t.tableId === table.tableId)) {
          // deselect table
          this.store.dispatch({ type: TablesPlanActions.deselectAllocatedTable.type, table });
        } else {
          // select table
          this.store.dispatch({ type: TablesPlanActions.selectAllocatedTable.type, table });
        }

      } else if (this.assignCategoriesToTablesMode || this.isOnExcludeFromOnlineChannelsMode) {

        if (this.allocatedTables.some(t => t.tableId === table.tableId)) {
          // deselect table
          this.store.dispatch({ type: TablesPlanActions.deselectAllocatedTable.type, table });
        } else {
          // select table
          this.store.dispatch({ type: TablesPlanActions.selectAllocatedTable.type, table });
        }

      } else {
        if (this.editingTable != null) {
          return;
        }

        this.setEditingTable(table);
      }
    } else if (this.reservationAssignTablesMode) {
      if (this.allocatedTables.some(t => t.tableId === table.tableId)) {
        // deselect table
        this.store.dispatch({ type: TablesPlanActions.deselectAllocatedTable.type, table });
      } else {
        // first click, deselect all others
        if (this.resetAllocatedTablesOnSelection) {
          this.store.dispatch({ type: TablesPlanActions.setAllocatedTables.type, tables: [table] });
          this.store.dispatch({ type: TablesPlanActions.setResetAllocatedTablesOnSelection.type, value: false });
        } else {
          // select table
          this.store.dispatch({ type: TablesPlanActions.selectAllocatedTable.type, table });
        }
      }
    } else {
      if (this.selectedTables.some(t => t.tableId === table.tableId)) {
        // deselect table
        this.store.dispatch({ type: TablesPlanActions.deselectTable.type, table });
      } else {
        if (this.reservationAssignTablesMode) {
          // assign tables mode - select table
          this.store.dispatch({ type: TablesPlanActions.selectTable.type, table });
        } else {
          // filter reservations mode - select it and deselect all others
          this.store.dispatch({ type: TablesPlanActions.setSelectedTables.type, tables: [table] });
        }
      }
    }
  }

  setEditingTable(table: Table) {
    this.store.dispatch({ type: TablesPlanActions.setEditingTable.type, table, isNew: false });

    try {
      $('#assignOnlineCategoryDropdown').dropdown('hide');
      $('#excludeFromOnlineChannelsDropdown').dropdown('hide');
    } catch (error) {
      console.error(error);
    }
  }

  dragStarted(event: DragStartEvent) {
    this.dragging = true;
    // this.sectionTables.push(event.table);
    // this.setEditingTable(event.table);
  }

  dragMoved(event: DragMoveEvent) {
    this.updateDragHelpers(event.table);
  }

  dragEnded(event: DragEndEvent) {
    this.dragging = false;
    const { distance } = event.event;
    const { x, y } = distance; // drag distance
    // this.sectionTables.pop();

    // reset draggable, remove transform: translate
    event.event.source._dragRef.reset();

    const tableX = this.tablesPlanService.coordinateToPixels(event.table.x);
    const tableY = this.tablesPlanService.coordinateToPixels(event.table.y);

    this.store.dispatch({
      type: TablesPlanActions.setTablePosition.type,
      table: event.table,
      x: this.tablesPlanService.pixelsToCoordinate(tableX + x / this.zoom),
      y: this.tablesPlanService.pixelsToCoordinate(tableY + y / this.zoom),
      typeOfModification: 'EDITED',
      modifiedBy: this.loggedInemployee?.surname
    });
  }

  tableIsSelected(table: Table) {
    if (this.editMode) {
      if (this.addJoinedTablesMode || this.assignCategoriesToTablesMode || this.isOnExcludeFromOnlineChannelsMode) {
        return this.allocatedTables.some(t => t.tableId === table.tableId);
      }

      return this.editingTable?.table.tableId == table.tableId;
    }
    if (this.reservationAssignTablesMode) {
      return this.allocatedTables.some(t => t.tableId === table.tableId);
    }
    return this.selectedTables.some(t => t.tableId === table.tableId);
  }

  updateDragHelpers(table: Table) {
    const el = this.tablesRef.find(t => t.table?.tableId === table.tableId);
    const rect = this.tableConnectionsService.relativeRect(el?.tableImageRect ?? null, this.canvasRect, this.canvasScrollDistance);

    const canvasScrollTop = this._canvas?.nativeElement.scrollTop;
    const canvasScrollLeft = this._canvas?.nativeElement.scrollLeft;

    const offset = 5;
    this.dragTableHelpers = [
      { left: canvasScrollLeft, top: (rect?.top ?? 0) - offset, isVertical: false },
      { left: canvasScrollLeft, top: (rect?.top ?? 0) + (rect?.height ?? 0) / 2, isVertical: false },
      { left: canvasScrollLeft, top: (rect?.top ?? 0) + (rect?.height ?? 0) + offset, isVertical: false },
      { left: (rect?.left ?? 0) - offset, top: canvasScrollTop, isVertical: true },
      { left: (rect?.left ?? 0) + (rect?.width ?? 0) / 2, top: canvasScrollTop, isVertical: true },
      { left: (rect?.left ?? 0) + (rect?.width ?? 0) + offset, top: canvasScrollTop, isVertical: true },
    ];
  }

  setAssignTablesMode() {
    if (this.reservationAssignTablesMode !== true) {

    } else {
      if (this.assignTablesReservation == null) {
        return;
      }

      let tableNames = (this.assignTablesReservation?.reservationDetails?.allocatedTables ?? '')
        .split(',')
        .filter(id => !Common.isNullOrEmpty(id));

      const tables = this.sectionTables.filter(t => tableNames?.includes(t.tableName));

      this.store.dispatch({ type: TablesPlanActions.setAllocatedTables.type, tables });
    }
  }

  setTablesPlanMode(editMode: boolean) {
    const restaurantId = this.storage.getRestaurantId();
    this.store.dispatch({ type: TablesPlanActions.setMode.type, editMode, restaurantId, modifiedBy: this.loggedInemployee?.surname, workingDate: this.date });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
